import usePageMeta from '../hooks/usePageMeta';
import { opt } from '../lib/media';
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, Minus, Plus, Truck, ShieldCheck, RefreshCw, ChevronRight, Play } from 'lucide-react';
import { getProduct, getByCategory, categoryMap } from '../mock';
import { fetchProduct, fetchProducts, fetchClientReviews, fetchReviews } from '../api';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/ProductCard';
import Reviews from '../components/Reviews';
import ClientReviewsWall from '../components/ClientReviewsWall';
import SizeChart from '../components/SizeChart';
import { toast } from 'sonner';
import { inr } from '../lib/currency';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const waCustomOrder = (product) => {
  const url = `${window.location.origin}/product/${product.id}`;
  const text = `Hi Hitera! I'd like to place a custom order for the ${product.name} (${url}). Could you guide me on measurements and the process?`;
  return `https://wa.me/919653629294?text=${encodeURIComponent(text)}`;
};

function useProductSchema(product) {
  useEffect(() => {
    if (!product?.id) return undefined;
    let el;
    Promise.all([fetchClientReviews({ productId: product.id, limit: 200 }).catch(() => []), fetchReviews(product.id).catch(() => [])]).then(([a, b]) => {
      const all = [...(a || []), ...(b || [])];
      const url = `https://hitera.co.in/product/${product.id}`;
      const img = (product.images || []).map((u) => (u.startsWith('/') ? `https://hitera.co.in${u}` : u));
      const data = {
        '@context': 'https://schema.org', '@type': 'Product', name: product.name, sku: product.id, url, image: img,
        description: product.description || `${product.name} by HITÉRA`, brand: { '@type': 'Brand', name: 'HITÉRA' },
        offers: { '@type': 'Offer', url, priceCurrency: 'INR', price: product.price, availability: `https://schema.org/${product.stock > 0 ? 'InStock' : 'PreOrder'}` },
      };
      if (all.length) {
        data.aggregateRating = { '@type': 'AggregateRating', ratingValue: (all.reduce((s, r) => s + r.rating, 0) / all.length).toFixed(1), reviewCount: all.length, bestRating: 5, worstRating: 1 };
        data.review = all.slice(0, 5).map((r) => ({ '@type': 'Review', author: { '@type': 'Person', name: r.name }, datePublished: r.date, reviewBody: r.experience || r.text, reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5 } }));
      }
      el = document.createElement('script');
      el.type = 'application/ld+json'; el.id = 'product-schema'; el.text = JSON.stringify(data);
      document.getElementById('product-schema')?.remove();
      document.head.appendChild(el);
    });
    return () => document.getElementById('product-schema')?.remove();
  }, [product]);
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [related, setRelated] = useState([]);
  const { addToCart, toggleWishlist, inWishlist } = useCart();
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState('');
  const [qty, setQty] = useState(1);
  usePageMeta(product ? `${product.name} | Hitera Designer Wear` : 'Hitera | Designer Wear', product ? (product.description || `Shop ${product.name} by Hitera — luxury designer wear from Mumbai, India.`) : undefined);

  useEffect(() => {
    setActiveImg(0); setSize(''); setQty(1); setLoaded(false); setProduct(null);
    fetchProduct(id)
      .then((p) => { if (p && p.id) setProduct(p); else setProduct(getProduct(id) || null); })
      .catch(() => setProduct(getProduct(id) || null))
      .finally(() => setLoaded(true));
  }, [id]);

  useProductSchema(product);

  useEffect(() => {
    if (product?.name) {
      sessionStorage.setItem('hitera_wa_context', JSON.stringify({ name: product.name, path: `/product/${product.id}` }));
    }
  }, [product]);

  useEffect(() => {
    if (!product) return;
    fetchProducts({ category: product.category })
      .then((list) => setRelated((Array.isArray(list) ? list : []).filter((p) => p.id !== product.id).slice(0, 4)))
      .catch(() => setRelated(getByCategory(product.category).filter((p) => p.id !== product.id).slice(0, 4)));
  }, [product]);

  if (!loaded) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 grid md:grid-cols-2 gap-10 lg:gap-16">
        <div className="aspect-[3/4] skeleton" />
        <div className="space-y-4 md:py-4">
          <div className="h-4 skeleton w-24" />
          <div className="h-12 skeleton w-3/4" />
          <div className="h-6 skeleton w-32" />
          <div className="h-24 skeleton w-full mt-6" />
          <div className="h-12 skeleton w-full mt-6" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="py-40 text-center">
        <h1 className="font-display text-4xl text-burgundy-900 mb-4">Piece not found</h1>
        <Link to="/shop" className="text-gold-dark underline">Return to shop</Link>
      </div>
    );
  }

  const cat = categoryMap[product.category] || { name: product.category };

  const handleAdd = () => {
    if (product.sizes.length > 1 && !size) {
      toast.error('Please select a size');
      return;
    }
    addToCart(product, size || product.sizes[0], qty);
    toast.success('Added to your bag', { description: `${product.name} · ${size || product.sizes[0]}` });
  };

  const wished = inWishlist(product.id);

  const media = [
    ...((product.videos || []).map((src) => ({ type: 'video', src }))),
    ...((product.images || []).map((src) => ({ type: 'image', src }))),
  ];
  const active = media[activeImg] || media[0] || { type: 'image', src: product.images && product.images[0] };

  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-6 flex items-center gap-2 text-xs tracking-wide text-burgundy-900/50">
        <Link to="/" className="hover:text-gold-dark">Home</Link> <ChevronRight size={13} />
        <Link to={`/shop/${product.category}`} className="hover:text-gold-dark">{cat?.name}</Link> <ChevronRight size={13} />
        <span className="text-burgundy-900">{product.name}</span>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 grid md:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="flex flex-col-reverse md:flex-row gap-4">
          <div className="flex md:flex-col gap-3">
            {media.map((m, idx) => (
              <button key={`${m.type}-${m.src}`} onClick={() => setActiveImg(idx)} className={`relative w-16 h-20 md:w-20 md:h-28 overflow-hidden border ${activeImg === idx ? 'border-gold' : 'border-transparent'}`}>
                {m.type === 'video' ? (
                  <>
                    <video src={m.src} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                    <span className="absolute inset-0 flex items-center justify-center bg-burgundy-950/30"><Play size={16} className="fill-cream text-cream" /></span>
                  </>
                ) : (
                  <img src={opt(m.src, 300)} alt={product.name} loading="lazy" decoding="async" width="80" height="112" className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-hidden bg-cream aspect-[3/4]">
            {active.type === 'video' ? (
              <video key={active.src} src={active.src} poster={opt(product.images && product.images[0], 1200)} className="w-full h-full object-cover" autoPlay muted loop playsInline controls preload="metadata" />
            ) : (
              <div className="img-zoom w-full h-full"><img src={opt(active.src, 1200)} alt={product.name} fetchPriority="high" decoding="async" width="1200" height="1600" className="w-full h-full object-cover" /></div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="md:py-4">
          <p className="text-[10px] tracking-luxe uppercase text-gold-dark mb-2">HITÉRA</p>
          <h1 className="font-display text-4xl md:text-5xl text-burgundy-900 leading-tight">{product.name}</h1>
          <div className="flex items-center gap-3 mt-4">
            <span className="text-2xl text-burgundy-800">{inr(product.price)}</span>
            {product.originalPrice && <span className="text-lg text-burgundy-900/40 line-through">{inr(product.originalPrice)}</span>}
          </div>
          <p className="text-[11px] tracking-wide text-burgundy-900/50 mt-1">Inclusive of all taxes · Custom-made to your measurements · Delivered in 2–3 weeks</p>

          <div className="gold-divider !mx-0 !my-7" />

          <p className="text-sm text-burgundy-900/70 leading-relaxed">{product.description}</p>

          {/* Colour */}
          <div className="mt-7">
            <p className="text-xs tracking-wideluxe uppercase text-burgundy-900 mb-3">Colour — <span className="text-gold-dark">{product.color}</span></p>
            <div className="flex gap-2">
              {product.colors.map((c) => (
                <span key={c} className="px-3 py-1.5 border border-burgundy-900/20 text-xs text-burgundy-900">{c}</span>
              ))}
            </div>
          </div>

          {/* Size */}
          {product.sizes.length > 1 && (
            <div className="mt-7">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs tracking-wideluxe uppercase text-burgundy-900">Select Size</p>
                <SizeChart trigger={<button data-testid="size-guide-btn" className="text-xs text-gold-dark underline tracking-wide">Size Guide</button>} />
              </div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} className={`min-w-[52px] px-4 py-3 border text-sm transition-colors ${size === s ? 'bg-burgundy-900 text-cream border-burgundy-900' : 'border-burgundy-900/25 text-burgundy-900 hover:border-gold'}`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {/* Qty + Add */}
          <div className="mt-8 flex gap-3">
            <div className="flex items-center border border-burgundy-900/25">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3.5 py-4 text-burgundy-900 hover:text-gold-dark"><Minus size={15} /></button>
              <span className="w-10 text-center text-burgundy-900">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3.5 py-4 text-burgundy-900 hover:text-gold-dark"><Plus size={15} /></button>
            </div>
            <button onClick={handleAdd} className="flex-1 bg-burgundy-900 text-cream text-xs tracking-wideluxe uppercase hover:bg-burgundy-800 transition-colors">Add To Bag</button>
            <button onClick={() => { toggleWishlist(product); toast(wished ? 'Removed from wishlist' : 'Saved to wishlist'); }} className="w-14 border border-burgundy-900/25 flex items-center justify-center hover:border-gold transition-colors">
              <Heart size={18} className={wished ? 'fill-burgundy-800 text-burgundy-800' : 'text-burgundy-900'} />
            </button>
          </div>

          {/* Custom order */}
          <a href={waCustomOrder(product)} target="_blank" rel="noreferrer" data-testid="custom-order-btn" className="mt-3 flex items-center justify-center gap-2 w-full border border-[#25D366] text-[#1da851] py-4 text-xs tracking-wideluxe uppercase hover:bg-[#25D366] hover:text-white transition-colors">
            <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24z"/></svg>
            Custom Order On WhatsApp
          </a>
          <p className="mt-3 text-xs text-burgundy-900/60 text-center leading-relaxed">✶ Every piece is custom-made — we'll tailor it to your exact measurements. Delivered in 2–3 weeks.</p>

          {/* Assurance */}
          <div className="mt-8 grid grid-cols-3 gap-3 text-center">
            {[{ i: Truck, t: 'Free Shipping Across India' }, { i: RefreshCw, t: 'Easy Returns' }, { i: ShieldCheck, t: 'Authentic Craft' }].map(({ i: Icon, t }) => (
              <div key={t} className="flex flex-col items-center gap-2 py-4 bg-cream/50">
                <Icon size={20} className="text-gold-dark" />
                <span className="text-[10px] tracking-wide text-burgundy-900/70 uppercase">{t}</span>
              </div>
            ))}
          </div>

          {/* Details accordion */}
          <Accordion type="single" collapsible className="mt-8">
            <AccordionItem value="details" className="border-gold/20">
              <AccordionTrigger className="text-xs tracking-wideluxe uppercase text-burgundy-900">Product Details</AccordionTrigger>
              <AccordionContent className="text-sm text-burgundy-900/70">
                <ul className="space-y-2">
                  <li>Fabric: {product.fabric}</li>
                  <li>Colour: {product.color}</li>
                  <li>Style Code: {product.id}</li>
                  <li>Hand-crafted by master karigars in India</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="ship" className="border-gold/20">
              <AccordionTrigger className="text-xs tracking-wideluxe uppercase text-burgundy-900">Shipping & Returns</AccordionTrigger>
              <AccordionContent className="text-sm text-burgundy-900/70">Each piece is custom-made to your measurements and delivered within 2–3 weeks. Ready-to-Ship items dispatch in 2–3 business days. Complimentary shipping across India.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
          <h2 className="font-display text-3xl md:text-4xl text-burgundy-900 text-center mb-10">You May Also Love</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {related.map((p, idx) => <ProductCard key={p.id} product={p} index={idx} />)}
          </div>
        </section>
      )}
      <ClientReviewsWall productId={product.id} title="Worn By Our Clients" eyebrow="Client Photos & Reviews" />
      <Reviews productId={product.id} />
    </div>
  );
}
